import { Link } from 'react-router-dom'
import { P, H, OL, UL, Li, Callout, Code, Screenshot } from '../components'

// ─── Last verified: 16 May 2026 (2 passes) ──────────────────────────────────
// Verified against:
//   - src/pages/Dashboard.js
//     Archive button is on each client row directly on the dashboard.
//     Label: "📦 Archive". Restore is "↩ Restore" on archived rows.
//     Archive modal has 3 buttons:
//       "Don't archive" (cancel)
//       "Archive only" (keep open requests intact but archive the client)
//       "Cancel N requests and archive" (cascade-cancel)
//     Archived clients list is on the dashboard itself, in a collapsed
//     section below the active list. Not a separate page or filter.
//   - src/lib/supabase.js uploadReceipt
//     Hard guard: archived client = upload links return error. Even if
//     the bookkeeper picked "Archive only", the client can no longer
//     upload. The "no longer active" page renders on the client portal.
//   - supabase/functions/process-reminders/index.ts
//     SELECT pulls client_archived; the find_due_reminders RPC excludes
//     archived clients. Toast and modal text both say "won't receive
//     automatic reminders".
//   - Hard-delete button (🗑) IS surfaced on every dashboard row for
//     Owners and Admins. Calls deleteClient which deletes the clients
//     row; FK cascades to requests, transactions, and attachments.
//     Storage files (receipts) are NOT cleaned up by this code path
//     so they become orphans -- worth flagging to user as a separate
//     product issue but not detailed in the article.
//   - Earlier wording "Receipt Chaser deliberately doesn't surface a
//     Delete client option" was wrong; corrected.
// Removed:
//   - "Archive from the menu on the client detail page" -- actually the
//     button is on each row directly on the dashboard.
//   - "Archived filter on a Clients page" -- there isn't a separate
//     Clients page; archived appear in a collapsible section on the
//     dashboard itself.
//   - "Leave them open. The client can still finish uploading" -- false.
//     Archived clients can never upload regardless of which option the
//     bookkeeper picked. "Archive only" keeps records intact on the
//     bookkeeper's side but stops the client side too.
//   - "Pro-rated to the day" billing claim -- proration_behavior is none
//     on plan switches; the billing happens at the 28th-of-month snapshot.
//   - "Long period of inactivity, request closes itself" -- no such
//     auto-close behaviour exists.
// Pass 2 added:
//   - Archived clients are excluded from auto-reminders
//   - Restore button is one click on the archived row, not a menu
//   - The "no longer active" page the client sees if they hit the link
//   - Clients can't currently be hard-deleted via the UI (no UI for it)

export default {
  title:   'Archiving and reopening clients',
  summary: "Tidying up the dashboard when you stop working with a client, and bringing one back when work resumes.",
  body: (
    <>
      <P>
        When you stop working with a client, archive them rather than
        delete them. Archiving keeps your records intact (every past
        request, every uploaded receipt, every lost-receipt
        declaration) while removing the client from your day-to-day
        view. On the per-client plan, archived clients drop off your
        billing too.
      </P>

      <Callout variant="info" title="Archive vs delete">
        A red 🗑 icon appears at the end of each client row on the
        dashboard for Owners and Admins (Assistants don't see it).
        Clicking it permanently removes the client, all their
        requests, and all their transaction records, after a single
        confirmation prompt. There's no undo. Use it only when you're
        certain (typically a test client you set up while learning
        the product). For every other situation, archive instead.
        Archive preserves your records, which matters because HMRC
        expects bookkeeping records kept for several years.
      </Callout>

      <H>When to archive a client</H>
      <UL>
        <Li>The client has left your practice.</Li>
        <Li>The business has closed or been struck off.</Li>
        <Li>The relationship is on pause and you're not expecting to send requests for a while.</Li>
        <Li>You set up a test client to learn the product and you're done with it.</Li>
      </UL>

      <H>How to archive a client</H>
      <OL>
        <Li>
          On the dashboard, find the client you want to archive in the
          active client list.
        </Li>
        <Li>
          Click <Code>📦 Archive</Code> on their row.
        </Li>
        <Li>
          If the client has no open requests, the archive happens
          immediately. If they do, a modal opens so you can choose
          what happens to those requests (see the next section).
        </Li>
      </OL>

      <Screenshot
        src="/support-screenshots/09 - archive-client.jpg"
        alt="The dashboard with three active client rows, the 📦 Archive button on one row highlighted in an orange outline. Other rows show + New request, View requests, and a small delete icon."
        caption="The Archive button on a client row, alongside the active client list on the dashboard."
      />

      <H>What happens to in-progress requests when you archive</H>
      <P>
        If the client has open requests when you archive them, you'll
        see a modal explaining the situation, with three options:
      </P>
      <UL>
        <Li>
          <strong>Don't archive.</strong> Cancels the archive action.
          Use this if you want to finish the open requests first and
          come back later.
        </Li>
        <Li>
          <strong>Archive only.</strong> The client is archived but
          the open requests are kept on your side as part of the
          historical record. Note: this still stops the client from
          uploading. The upload link they were given now shows a "no
          longer accepting uploads" page.
        </Li>
        <Li>
          <strong>Cancel all open requests and archive.</strong> Cancels
          every open request as well as archiving the client. Files
          already uploaded are kept; each request moves to the
          Cancelled tab on the client's history. The button shows the
          exact number you'd be cancelling before you click.
        </Li>
      </UL>

      <Callout variant="warning" title="Archived = client can't upload">
        Either choice (Archive only or cascade-cancel) stops the
        client from uploading. The difference is purely how the
        request appears in your records: kept as outstanding-with-no-uploads
        (Archive only) or moved to cancelled (cascade-cancel). If you
        need the client to finish uploading, don't archive yet.
      </Callout>

      <H>What happens to auto-reminders</H>
      <P>
        Archived clients are excluded from automatic reminders. Any
        scheduled reminder that hadn't fired yet won't fire. If you
        reopen the client later, reminders work normally again from
        that point.
      </P>

      <H>Billing after archiving</H>
      <P>
        On the per-client plan, archived clients don't count toward
        your bill. Your bill is calculated from your active client
        count on the 28th of each month, so the saving shows up on
        the next billing cycle, not immediately. There is no
        pro-rating: archiving on the 1st or the 27th has the same
        effect on the upcoming bill.
      </P>
      <P>
        On the Pro plan, billing doesn't change with client count
        (it's a flat £25/month regardless), but archiving still helps
        keep the dashboard tidy.
      </P>

      <H>Finding archived clients</H>
      <P>
        Archived clients are listed on the dashboard itself, not on a
        separate page:
      </P>
      <OL>
        <Li>
          On the dashboard, scroll past the active client list.
        </Li>
        <Li>
          Click the <Code>▶ Archived clients ({'{n}'})</Code> link to
          expand the section.
        </Li>
        <Li>
          The archived clients appear below. The dashboard search box
          searches both active and archived lists, so you can find a
          specific archived client by name or email even with the
          section collapsed.
        </Li>
      </OL>

      <Screenshot
        src="/support-screenshots/09 - archived-list.jpg"
        alt="The dashboard with the Archived clients (1) disclosure expanded below the active client list, showing one archived client (Clearview Window Cleaning) with the Archived badge and a Restore button. A help line above reads 'Archived clients are not counted toward your billing. You can still view their requests.'"
        caption="The dashboard with the Archived clients section expanded below the active list, showing one archived client with the Restore button."
      />

      <H>Reopening (restoring) a client</H>
      <P>
        Picking work back up with a former client is a single click.
      </P>
      <OL>
        <Li>Open the Archived clients section on your dashboard.</Li>
        <Li>
          Click <Code>↩ Restore</Code> on the client's row.
        </Li>
        <Li>
          The client moves back to the active list immediately. On
          the per-client plan, they count toward your bill again from
          the next 28th-of-month billing snapshot.
        </Li>
      </OL>
      <P>
        Restoring a client doesn't automatically reopen any requests
        that were cancelled when you archived them. Each request can
        be reopened individually from the request page if needed (see{' '}
        <Link to="/support/resending-or-editing-a-request">Resending or editing a request</Link>),
        or you can just send a fresh request.
      </P>

      <H>Reopening doesn't refresh the contact details</H>
      <P>
        If you've been away from this client for a while, the contact
        details on file (email address, contact name, internal
        reference) might be out of date. Worth opening their profile
        and checking everything is current before sending the first
        request after restoring.
      </P>

      <H>Related guides</H>
      <UL>
        <Li>
          <Link to="/support/adding-your-first-client">Adding your first client</Link>
        </Li>
        <Li>
          <Link to="/support/resending-or-editing-a-request">Resending or editing a request</Link>
        </Li>
        <Li>
          <Link to="/support/changing-or-cancelling-your-plan">Changing or cancelling your plan</Link>
        </Li>
      </UL>
    </>
  ),
}
